import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface User {
  id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  entity_id: number | null;
  profile: string | null;
  status: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface UserFilterOption {
  value: string;
  label: string;
  avatar_url?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}
// Tipos de respuesta
interface UsersResponse {
  data: User[];
  totalCount: number;
}
interface UsersState extends FilteredState<User> {
  selectedUser: User | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: UserFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: UsersState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'username',
  sortDirection: 'asc',
  searchTerm: null,
  selectedUser: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar users con paginación y filtros (para vistas de lista)
export const fetchUsers = createAsyncThunk<UsersResponse, any>('users/fetchUsers', async (params) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, nombre, email, avatar) - CACHEADO
export const fetchUserFilterOptions = createAsyncThunk<UserFilterOption[], void>(
  'users/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const usersState = state.users as UsersState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      usersState.filterOptionsLastFetch &&
      Date.now() - usersState.filterOptionsLastFetch < CACHE_DURATION &&
      usersState.filterOptions.length > 0
    ) {
      return usersState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/users?startRow=0&endRow=500&sortProperty=username&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const users = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: UserFilterOption[] = users.map((user: any) => ({
        value: user.id.toString(),
        label: user.username,
        avatar_url: user.avatar_url,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchUserById = createAsyncThunk<User, number>('users/fetchUserById', async (id) => {
  throw new Error('Not implemented yet');
});
export const createUser = createAsyncThunk<User, Partial<User>>('users/createUser', async (user) => {
  throw new Error('Not implemented yet');
});
export const updateUser = createAsyncThunk<{ id: number; data: User }, { id: number; data: Partial<User> }>('users/updateUser', async ({ id, data }) => {
  throw new Error('Not implemented yet');
});
export const deleteUser = createAsyncThunk<number, number>('users/deleteUser', async (id) => {
  throw new Error('Not implemented yet');
});
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    clearSelection: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchUsers (lista completa)
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar usuarios';
      })
      // Casos para fetchUserFilterOptions (opciones cacheadas)
      .addCase(fetchUserFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchUserFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchUserFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectUserFilterOptions = (state: any) => state.users.filterOptions;
export const selectUserFilterOptionsLoading = (state: any) => state.users.filterOptionsLoading;
export const selectUserFilterOptionsError = (state: any) => state.users.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areUserFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.users.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectUser, clearSelection } = usersSlice.actions;
export default usersSlice.reducer;
